import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from 'react';
import {
  Check,
  ChevronDown,
  Loader2,
  ShoppingBag,
  Tag,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { timeAgo } from '@ampersandbrands/ui';
import { useStoreMe, useEffectiveStoreNo, isNotAvailable } from '@/lib/useStores';
import {
  useItemSearch,
  usePulseFlags,
  useSubmitFlag,
  useTopItems,
  lookupBarcodeItem,
  type PulseItem,
  type PulseSignal,
  type PulseFlag,
} from '@/lib/usePulse';
import { BarcodeScanner, type BarcodeScanResult } from '@/components/BarcodeScanner';
import { reportScannerEvent } from '@/lib/reportScannerEvent';

// --- Shared UI atoms ---

function CardShell({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark">
        {icon}
      </span>
      <span className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
        {title}
      </span>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-candy-line bg-candy-white px-3 py-2 text-[15px] ' +
  'text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none ' +
  'dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink ' +
  'dark:placeholder:text-candy-dark-ink-muted dark:focus:border-candy-pink-dark';

function EmptyState({ message }: { message: string }) {
  return (
    <p className="py-4 text-center text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
      {message}
    </p>
  );
}

// --- Signal button ---

const SIGNALS: { value: PulseSignal; label: string; colorClass: string }[] = [
  {
    value: 'too_much',
    label: 'Too much',
    colorClass:
      'border-candy-pink bg-candy-pink/10 text-candy-pink dark:border-candy-pink-dark dark:bg-candy-pink-dark/10 dark:text-candy-pink-dark',
  },
  {
    value: 'send_more',
    label: 'Send more',
    colorClass:
      'border-candy-teal bg-candy-teal/10 text-candy-teal dark:border-candy-teal-dark dark:bg-candy-teal-dark/10 dark:text-candy-teal-dark',
  },
  {
    value: 'requested',
    label: 'People are asking',
    colorClass:
      'border-candy-gold bg-candy-gold/10 text-candy-gold dark:border-candy-gold-dark dark:bg-candy-gold-dark/10 dark:text-candy-gold-dark',
  },
  {
    value: 'problem',
    label: 'Problem',
    // Semantic `danger` token (not a candy-* brand hue): its CSS variable
    // already flips value under `.dark`, so no dark: variant is needed.
    colorClass: 'border-danger bg-danger/10 text-danger',
  },
  {
    value: 'other',
    label: 'Other',
    colorClass:
      'border-candy-lavender bg-candy-lavender/10 text-candy-lavender dark:border-candy-lavender-dark dark:bg-candy-lavender-dark/10 dark:text-candy-lavender-dark',
  },
];

function signalChipClass(signal: PulseSignal): string {
  switch (signal) {
    case 'too_much':
      return 'bg-candy-pink/10 text-candy-pink dark:bg-candy-pink-dark/10 dark:text-candy-pink-dark';
    case 'send_more':
      return 'bg-candy-teal/10 text-candy-teal dark:bg-candy-teal-dark/10 dark:text-candy-teal-dark';
    case 'requested':
      return 'bg-candy-gold/10 text-candy-gold dark:bg-candy-gold-dark/10 dark:text-candy-gold-dark';
    case 'problem':
      return 'bg-danger/10 text-danger';
    case 'other':
      return 'bg-candy-lavender/10 text-candy-lavender dark:bg-candy-lavender-dark/10 dark:text-candy-lavender-dark';
  }
}

function signalLabel(signal: PulseSignal): string {
  switch (signal) {
    case 'too_much':
      return 'Too much';
    case 'send_more':
      return 'Send more';
    case 'requested':
      return 'People are asking';
    case 'problem':
      return 'Problem';
    case 'other':
      return 'Other';
  }
}

// --- Flag composer ---

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function FlagComposer({ storeNo }: { storeNo?: number }) {
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setSelectedItem] = useState<PulseItem | null>(null);
  const [signal, setSignal] = useState<PulseSignal | null>(null);
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanNoMatch, setScanNoMatch] = useState(false);

  const q = useDebounce(searchText, 300);
  const searchQ = useItemSearch(q, storeNo);
  const submitMut = useSubmitFlag(storeNo);

  const canSubmit = selectedItem !== null && signal !== null;

  function handleSelectItem(item: PulseItem) {
    setSelectedItem(item);
    setSearchText('');
    setScanNoMatch(false);
  }

  const handleBarcodeFound = useCallback(async (code: string, scan: BarcodeScanResult) => {
    setScanLoading(true);
    setScanNoMatch(false);
    const startedAt = Date.now();
    try {
      const item = await lookupBarcodeItem(code, storeNo);
      reportScannerEvent({
        type: item ? 'lookup_found' : 'lookup_not_found',
        source: scan.source,
        storeNo,
        barcodeLength: code.length,
        durationMs: Date.now() - startedAt,
      });
      if (item) {
        setSelectedItem(item);
      } else {
        setScanNoMatch(true);
      }
    } catch {
      reportScannerEvent({
        type: 'lookup_failed',
        source: scan.source,
        storeNo,
        barcodeLength: code.length,
        durationMs: Date.now() - startedAt,
      });
      setScanNoMatch(true);
    } finally {
      setScanLoading(false);
    }
  }, [storeNo]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submitMut.mutate(
      { item_no: selectedItem.item_no, signal, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setSelectedItem(null);
          setSignal(null);
          setNote('');
          setSearchText('');
          setScanNoMatch(false);
          setSubmitted(true);
          setTimeout(() => setSubmitted(false), 3000);
        },
      },
    );
  }

  const showResults =
    q.length >= 2 && !selectedItem && !searchQ.isLoading && (searchQ.data?.length ?? 0) > 0;
  const showNoResults =
    q.length >= 2 && !selectedItem && !searchQ.isLoading && (searchQ.data?.length ?? 0) === 0;

  return (
    <CardShell>
      <CardHeader icon={<Tag size={16} aria-hidden />} title="Flag an item" />
      <form onSubmit={handleSubmit} className="divide-y divide-candy-line dark:divide-candy-dark-line">
        {/* Search / selected item */}
        <div className="px-4 py-3">
          {selectedItem ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-candy-line bg-candy-cream-warm px-3 py-2 dark:border-candy-dark-line dark:bg-candy-dark-surface-warm">
              <div className="min-w-0">
                <p className="truncate text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
                  {selectedItem.descr}
                </p>
                <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  #{selectedItem.item_no} · On hand: {selectedItem.qty_on_hnd}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedItem(null); setScanNoMatch(false); }}
                className="shrink-0 text-[12px] text-candy-ink-muted underline-offset-2 hover:text-candy-pink hover:underline dark:text-candy-dark-ink-muted dark:hover:text-candy-pink-dark"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <BarcodeScanner onBarcodeFound={handleBarcodeFound} storeNo={storeNo} />
              {scanLoading && (
                <p className="mb-2 flex items-center gap-1.5 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  <Loader2 size={12} className="animate-spin" aria-hidden /> Looking up barcode…
                </p>
              )}
              {scanNoMatch && (
                <p className="mb-2 text-[12px] text-candy-pink dark:text-candy-pink-dark">
                  No item found for this barcode.
                </p>
              )}
              <div className="relative">
                <input
                  type="search"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setScanNoMatch(false); }}
                  placeholder="Search by name or item #…"
                  className={inputCls}
                  autoComplete="off"
                />
                {searchQ.isLoading && (
                  <Loader2
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-candy-ink-muted dark:text-candy-dark-ink-muted"
                    aria-hidden
                  />
                )}
                {showResults && (
                  <ul className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
                    {searchQ.data!.map((item) => (
                      <li key={item.item_no}>
                        <button
                          type="button"
                          onClick={() => handleSelectItem(item)}
                          className="flex w-full flex-col px-3 py-2 text-left hover:bg-candy-cream-warm dark:hover:bg-candy-dark-surface-warm"
                        >
                          <span className="text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
                            {item.descr}
                          </span>
                          <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                            #{item.item_no} · On hand: {item.qty_on_hnd}{item.price != null ? ` · $${item.price.toFixed(2)}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {showNoResults && (
                  <p className="mt-1 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                    No items found for "{q}"
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Signal buttons */}
        <div className="px-4 py-3">
          <p className="mb-2 text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Signal
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            {SIGNALS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setSignal(s.value)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                  signal === s.value
                    ? s.colorClass
                    : 'border-candy-line text-candy-ink-muted hover:border-candy-ink-muted dark:border-candy-dark-line dark:text-candy-dark-ink-muted dark:hover:border-candy-dark-ink-muted'
                }`}
              >
                {signal === s.value && <Check size={13} aria-hidden />}
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div className="px-4 py-3">
          <label
            htmlFor="pulse-note"
            className="mb-1 block text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted"
          >
            Note{' '}
            <span className="font-normal">
              {signal === 'other' ? "(please describe what's going on)" : '(optional)'}
            </span>
          </label>
          <textarea
            id="pulse-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={signal === 'other' ? "What's going on with this item?" : 'Any context to add…'}
            className={`${inputCls} resize-none`}
          />
        </div>

        {/* Submit row */}
        <div className="px-4 py-3">
          {submitted && (
            <p className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-candy-teal dark:text-candy-teal-dark">
              <Check size={14} aria-hidden /> Flag submitted!
            </p>
          )}
          {submitMut.isError && (
            <p className="mb-2 text-[12px] text-red-500 dark:text-red-400">
              Failed to submit. Try again.
            </p>
          )}
          <button
            type="submit"
            disabled={!canSubmit || submitMut.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-candy-pink px-4 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
          >
            {submitMut.isPending && <Loader2 size={15} className="animate-spin" aria-hidden />}
            {submitMut.isPending ? 'Submitting…' : 'Submit flag'}
          </button>
        </div>
      </form>
    </CardShell>
  );
}

// --- Recent flags ---

function FlagRow({ flag }: { flag: PulseFlag }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <span
        className={`mt-0.5 shrink-0 rounded-pill px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${signalChipClass(flag.signal)}`}
      >
        {signalLabel(flag.signal)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
          {flag.descr}
        </p>
        {flag.note && (
          <p className="mt-0.5 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            {flag.note}
          </p>
        )}
        {flag.status === 'acknowledged' && (
          <span
            className="mt-1.5 inline-flex items-center gap-1 rounded-pill bg-candy-teal/10 px-2 py-0.5 text-[11px] font-semibold text-candy-teal dark:text-candy-teal-dark"
            title={
              flag.acknowledged_at
                ? `Acknowledged by HQ ${timeAgo(flag.acknowledged_at)}`
                : 'Acknowledged by HQ'
            }
          >
            <Check size={12} aria-hidden />
            Acknowledged by HQ
            {flag.acknowledged_at && (
              <span className="font-normal opacity-80">{timeAgo(flag.acknowledged_at)}</span>
            )}
          </span>
        )}
      </div>
      <span className="shrink-0 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {timeAgo(flag.created_at)}
      </span>
    </div>
  );
}

function RecentFlags({ storeNo }: { storeNo?: number }) {
  const [expanded, setExpanded] = useState(false);
  const flagsQ = usePulseFlags(storeNo);
  const flags = flagsQ.data ?? [];
  const visible = expanded ? flags : flags.slice(0, 5);

  return (
    <CardShell>
      <CardHeader icon={<ShoppingBag size={16} aria-hidden />} title="Your recent flags" />
      <div className="px-4">
        {flagsQ.isLoading && (
          <div className="space-y-3 py-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-10 animate-pulse rounded-md bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
              />
            ))}
          </div>
        )}
        {!flagsQ.isLoading && flags.length === 0 && (
          <EmptyState message="No flags submitted yet — use the composer above to flag your first item." />
        )}
        {!flagsQ.isLoading && flags.length > 0 && (
          <div className="divide-y divide-candy-line dark:divide-candy-dark-line">
            {visible.map((flag) => (
              <FlagRow key={flag.id} flag={flag} />
            ))}
          </div>
        )}
        {flags.length > 5 && (
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="flex w-full items-center justify-center gap-1 py-2 text-[13px] text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
          >
            <ChevronDown
              size={14}
              className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
            {expanded ? 'Show less' : `Show ${flags.length - 5} more`}
          </button>
        )}
      </div>
    </CardShell>
  );
}

// --- Top / slow sellers ---

function ItemListRow({ rank, item }: { rank: number; item: { item_no: string; descr: string } }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-5 shrink-0 text-center text-[12px] font-semibold text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] text-candy-ink dark:text-candy-dark-ink">{item.descr}</p>
        <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">#{item.item_no}</p>
      </div>
    </div>
  );
}

function SellersCard({ storeNo }: { storeNo?: number }) {
  const topQ = useTopItems(storeNo);

  return (
    <div className="space-y-4">
      {/* Top sellers */}
      <CardShell>
        <CardHeader icon={<TrendingUp size={16} aria-hidden />} title="Top sellers this week" />
        <div className="px-4">
          {topQ.isLoading && (
            <div className="space-y-2 py-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-8 animate-pulse rounded-md bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
                />
              ))}
            </div>
          )}
          {!topQ.isLoading && !topQ.data?.top?.length && (
            <EmptyState message="Sales data not available yet." />
          )}
          {!topQ.isLoading && topQ.data?.top && topQ.data.top.length > 0 && (
            <div className="divide-y divide-candy-line dark:divide-candy-dark-line">
              {topQ.data.top.map((item, i) => (
                <ItemListRow key={item.item_no} rank={i + 1} item={item} />
              ))}
            </div>
          )}
        </div>
      </CardShell>

      {/* Slow movers */}
      <CardShell>
        <CardHeader icon={<TrendingDown size={16} aria-hidden />} title="Slow movers this week" />
        <div className="px-4">
          {topQ.isLoading && (
            <div className="space-y-2 py-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-8 animate-pulse rounded-md bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
                />
              ))}
            </div>
          )}
          {!topQ.isLoading && !topQ.data?.bottom?.length && (
            <EmptyState message="Sales data not available yet." />
          )}
          {!topQ.isLoading && topQ.data?.bottom && topQ.data.bottom.length > 0 && (
            <div className="divide-y divide-candy-line dark:divide-candy-dark-line">
              {topQ.data.bottom.map((item, i) => (
                <ItemListRow key={item.item_no} rank={i + 1} item={item} />
              ))}
            </div>
          )}
        </div>
      </CardShell>
    </div>
  );
}

// --- Page export ---

export function Pulse() {
  const activeStoreNo = useEffectiveStoreNo();

  const meQ = useStoreMe(activeStoreNo);
  const storeName = meQ.data?.store?.store_name;

  if (meQ.isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <div className="mb-5 h-10 w-48 animate-pulse rounded-lg bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
        <div className="space-y-4">
          {([0, 1, 2] as const).map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  if (meQ.error && !isNotAvailable(meQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">
            Merch Pulse
          </h1>
        </div>
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Could not load store data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">
          Merch Pulse
        </h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Flag items &amp; track what's moving
          {storeName && (
            <>
              {' · '}
              <span className="font-medium">{storeName}</span>
            </>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <FlagComposer storeNo={activeStoreNo} />
        <RecentFlags storeNo={activeStoreNo} />
        <SellersCard storeNo={activeStoreNo} />
      </div>
    </div>
  );
}
